import React from 'react';
import { Form, Select, Input, Announcement, Modal } from '@block/plug';
import { SuccessFile } from '@src/types/self';
import { doAction, getPattern } from '@utils/index';
import luhn from 'fast-luhn';
import { Upload } from '../../components';
import Url from './url';
import './index.scss';

interface Props {
  ref: any;
}
interface State {
  status: boolean;
}
class BankInfo extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.dataChanged = false;
    this.formRef = React.createRef();
    this.state = {
      status: false,
    };
  }

  dataChanged: boolean;

  formRef: React.RefObject<any>;

  async componentDidMount() {
    const data = (await doAction(Url.getInfo, {}, null)) || {};
    const { bankCountryDictIndex } = data;
    this.setState({
      status: bankCountryDictIndex !== 'CHN',
    });
    this.formRef.current?.setAllFieldsValue(data);
  }

  handleFetchNationality = async (filter = '') => {
    const data = await doAction(
      Url.getNationality,
      {
        filter,
      },
      null,
    );
    return data.pageList;
  };

  handleFetchProvince = async (filter = '') => {
    const data = await doAction(
      Url.getProvince,
      {
        filter,
        parentIndex: 'CHN',
      },
      null,
    );
    return data.pageList;
  };

  handleFetchCity = async (filter = '') => {
    const { bankProvinceDictIndex } = this.formRef.current.getFieldsValueIncludeHide();
    const data = await doAction(
      Url.getCity,
      {
        filter,
        parentExtIndex: 'CHN',
        parentIndex: bankProvinceDictIndex,
      },
      null,
    );
    return data.pageList;
  };

  handleFetchBank = async (filter = '') => {
    const data = await doAction(
      Url.getBank,
      {
        filter,
        parentIndex: 'CHN',
      },
      null,
    );
    return data.pageList;
  };

  handleFetchBranch = async (filter = '') => {
    const { bankDictIndex } = this.formRef.current.getFieldsValueIncludeHide();
    const data = await doAction(
      Url.getBranch,
      {
        filter,
        parentExtIndex: 'CHN',
        parentIndex: bankDictIndex,
      },
      null,
    );
    return data.pageList;
  };

  handleFieldValueChange = (code, value) => {
    const ref = this.formRef.current;
    if (code === 'bankCountryDictIndex') {
      this.setState({
        status: value !== 'CHN',
      });
      ref?.setFieldsValue({
        bankProvinceDictIndex: null,
        bankProvinceDictName: null,
        bankCityDictIndex: null,
        bankCityDictName: null,
      });
    }
    if (code === 'bankProvinceDictIndex') {
      ref?.setFieldsValue({
        bankCityDictIndex: null,
        bankCityDictName: null,
      });
    }
    if (code === 'bankDictIndex') {
      ref?.setFieldsValue({
        bankBranchDictIndex: null,
        bankBranchDictName: null,
      });
    }
    this.dataChanged = true;
  };

  handleSuccess = (file: SuccessFile) => {
    const { fileId, fileName, fileUrl } = file;
    const ref = this.formRef.current;
    ref?.setFieldsValue({
      bankCardUrl: fileUrl,
      bankCardId: fileId,
      bankCardName: fileName,
    });
    fileId && ref.clearValidateStatus(['bankCardUrl']);
  };

  // 银行卡luhn算法校验
  bankAccountLuhnValidator = () => {
    return new Promise((resolve) => {
      const { bankAccountNumber } = this.formRef.current?.getFieldsValue?.(['bankAccountNumber']) || {};
      if (/^62\d{14,17}$/.test(bankAccountNumber) && !luhn(bankAccountNumber)) {
        Modal.confirm({
          title: '提示',
          message: '银行卡号填写有误，请确认（如确认无误，请点击确定）',
          cancelText: '返回',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      } else {
        resolve(true);
      }
    });
  };

  handleSave = async () => {
    const ref = this.formRef.current;
    if (ref?.validateFields()) {
      const bankAccountValid = await this.bankAccountLuhnValidator();
      if (!bankAccountValid) return false;
      const data = await doAction(Url.save, this.handleFetchFinalData());
      return data === undefined ? false : data;
    }
    return false;
  };

  // 获取最终组织数据
  handleFetchFinalData = () => this.formRef.current.getFieldsValueIncludeHide();

  render() {
    const { ref } = this.props;
    const { status } = this.state;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    return (
      <div className="bank" ref={ref}>
        <div className="bank__announcement">
          <Announcement
            title={
              <div>
                <div>
                  1、请务必使用I类账户银行卡并提供准确的本人银行储蓄卡相关信息，否则将影响正常发薪。是否为I类账户及开户行信息可通过银行APP或拨打银行客服热线进行确认。因信息填写有误造成的损失由员工本人承担。
                </div>
                <div>
                  2、各家银行的I类储蓄卡均可使用，如您有招商银行、建设银行、中国银行、工商银行、农业银行、中信银行、平安银行的I类储蓄卡，可优先选择使用。
                </div>
              </div>
            }
            showIcon
            type="warning"
          />
        </div>
        <Form
          className="bank__form"
          ref={this.formRef}
          labelWidth="100px"
          onFieldValueChange={this.handleFieldValueChange}
        >
          <Form.Item
            formItemKey="bankCountryDictIndex"
            labelInValue={false}
            formItemLabel="bankCountryDictName"
            span={12}
            label="银行开户地："
            required
            message="不能为空"
          >
            <Select
              toFormItem
              filterable
              keyField="dictIndex"
              labelField="dictValue"
              onFetchData={(filter) => this.handleFetchNationality(filter)}
              placeholder="国家/地区"
              loadingMessage="正在搜索..."
              notFoundMessage="无数据"
              popLayer={{
                getContainer: () => document.querySelector('.bank__form'),
              }}
            />
          </Form.Item>
          <Form.Item
            className="bank__space"
            formItemKey="bankProvinceDictIndex"
            span={6}
            labelWidth={0}
            required
            message="不能为空"
            labelInValue={false}
            formItemLabel="bankProvinceDictName"
            hide={status}
          >
            <Select
              toFormItem
              filterable
              keyField="dictIndex"
              labelField="dictValue"
              onFetchData={(filter) => this.handleFetchProvince(filter)}
              placeholder="省"
              loadingMessage="正在搜索..."
              notFoundMessage="无数据"
              popLayer={{
                getContainer: () => document.querySelector('.bank__form'),
              }}
            />
          </Form.Item>
          <Form.Item
            className="bank__space"
            formItemKey="bankCityDictIndex"
            labelInValue={false}
            formItemLabel="bankCityDictName"
            span={6}
            labelWidth={0}
            required
            message="不能为空"
            hide={status}
          >
            <Select
              toFormItem
              filterable
              keyField="dictIndex"
              labelField="dictValue"
              onFetchData={(filter) => this.handleFetchCity(filter)}
              placeholder="市"
              loadingMessage="正在搜索..."
              notFoundMessage="无数据"
              popLayer={{
                getContainer: () => document.querySelector('.bank__form'),
              }}
            />
          </Form.Item>
          <Form.Item span={10} label="&nbsp;" hide={!status} />
          <Form.Item
            formItemKey="bankDictIndex"
            labelInValue={false}
            formItemLabel="bankDictName"
            label="银行开户行："
            required
            message="不能为空"
          >
            <Select
              toFormItem
              keyField="dictIndex"
              labelField="dictValue"
              filterable
              onFetchData={(filter) => this.handleFetchBank(filter)}
              placeholder="请输入关键字进行搜索"
              loadingMessage="正在搜索..."
              notFoundMessage=""
              popLayer={{
                getContainer: () => document.querySelector('.bank__form'),
              }}
            />
          </Form.Item>
          <Form.Item
            formItemKey="bankBranchDictIndex"
            labelInValue={false}
            formItemLabel="bankBranchDictName"
            label="银行支行："
            required
            message="不能为空"
          >
            <Select
              toFormItem
              keyField="dictIndex"
              labelField="dictValue"
              filterable
              onFetchData={(filter) => this.handleFetchBranch(filter)}
              placeholder="请输入关键字进行搜索"
              loadingMessage="正在搜索..."
              notFoundMessage=""
              popLayer={{
                getContainer: () => document.querySelector('.bank__form'),
              }}
            />
          </Form.Item>
          <Form.Item formItemKey="bankAccountName" label="银行账户名：" required message="不能为空">
            <Input toFormItem maxLength={50} />
          </Form.Item>
          <Form.Item
            formItemKey="bankAccountNumber"
            label="银行卡号："
            required
            rules={[
              { required: true, message: '银行卡号不能为空' },
              {
                validator(rule, value, callback) {
                  const { bankCountryDictIndex, bankDictIndex } = self.formRef.current.getFieldsValueIncludeHide();
                  let str;
                  let msg = '';
                  if (bankCountryDictIndex === 'CHN') {
                    if (bankDictIndex === 'CMB') {
                      str = '^([0-9]{12}|[0-9]{14}|[0-9]{15}|[0-9]{16}){1}$';
                      msg = '请输入正确的银行卡号';
                    } else {
                      str = '^[0-9]{1,35}$';
                      msg = '银行卡号只允许数字';
                    }
                  } else {
                    str = '^[0-9a-zA-Z]{1,35}$';
                    msg = '请输入正确的银行卡号';
                  }
                  const pattern = getPattern(str);
                  if (value && value.length > 35) {
                    callback('字数过长');
                  } else if (pattern && !pattern.test(value)) {
                    callback(msg);
                  } else {
                    callback();
                  }
                },
              },
            ]}
          >
            <Input toFormItem maxLength={50} />
          </Form.Item>
          <Form.Item formItemKey="bankCardUrl" label="银行卡照片：">
            <Upload toFormItem url={Url.upload} label="银行卡照片" onChange={(file) => this.handleSuccess(file)} />
          </Form.Item>
        </Form>
      </div>
    );
  }
}
export default BankInfo;
